import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Crown, Medal, Award, Sparkles } from 'lucide-react';
import BookCardCover from './BookCardCover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Podium colours for ranks 1/2/3 - gold, silver, bronze (warm, kid-friendly).
const PODIUM = {
  1: { ring: '#F5B400', bg: '#FFF4D1', label: 'Gold', Icon: Crown },
  2: { ring: '#A4A4A4', bg: '#F1F1F1', label: 'Silver', Icon: Medal },
  3: { ring: '#C57A3B', bg: '#FBE6D2', label: 'Bronze', Icon: Award },
};

function formatPeriodLabel(period) {
  // period === "YYYY-MM" → "January 2026"
  if (!period || typeof period !== 'string') return '';
  const [y, m] = period.split('-').map((p) => parseInt(p, 10));
  if (!y || !m) return period;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function LeaderboardSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/leaderboard/top-shared-books`);
        if (!cancelled) setData(r.data);
      } catch {
        // Silent on the public landing page; absence of leaderboard is fine.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  const leaders = data?.leaders || [];
  if (leaders.length === 0) return null; // hide the whole section when empty

  const topThree = leaders.slice(0, 3);
  const rest = leaders.slice(3);
  const prevWinners = data?.previous_winners || [];
  const monthLabel = data?.current_month_label || '';

  return (
    <section className="py-20" data-testid="leaderboard-section" style={{ background: '#FFFDF9' }}>
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Heading */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4"
               style={{ background: '#FFF4D1', color: '#8A6300' }}>
            <Trophy className="w-4 h-4" />
            <span className="text-xs font-semibold tracking-wide uppercase">Top Stories of the Month</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug mb-3"
              style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
            Most-Shared Storybooks {monthLabel ? `· ${monthLabel}` : ''}
          </h2>
          <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
            Ranked by unique friends reached via share links. Top 3 authors earn a badge + 1 free book credit, announced on the 1st of every month.
          </p>
        </div>

        {/* Previous month's settled winners - small banner */}
        {prevWinners.length > 0 && (
          <div
            className="rounded-2xl px-6 py-4 mb-10 border"
            data-testid="previous-month-winners"
            style={{ background: '#FFF9F0', borderColor: '#F3E8E0' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: '#8A6300' }}>
                <Sparkles className="w-4 h-4" />
                {formatPeriodLabel(data?.previous_period)} Champions
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm" style={{ color: '#4A5568' }}>
                {prevWinners.map((w) => {
                  const P = PODIUM[w.rank] || PODIUM[3];
                  return (
                    <span key={`${w.rank}-${w.book_id}`} className="inline-flex items-center gap-1.5">
                      <P.Icon className="w-4 h-4" style={{ color: P.ring }} />
                      <span className="font-medium" style={{ color: '#2D3748' }}>{w.title}</span>
                      <span className="opacity-70">— by {w.author_name}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Podium - Top 3 */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {topThree.map((b) => {
            const P = PODIUM[b.rank] || PODIUM[3];
            return (
              <Link
                key={b.book_id}
                to={`/book/${b.book_id}`}
                data-testid={`leaderboard-podium-${b.rank}`}
                className="group relative bg-white rounded-3xl overflow-hidden card-hover border"
                style={{ borderColor: P.ring, boxShadow: '0 12px 36px rgba(0, 0, 0, 0.06)' }}
              >
                {/* Rank chip */}
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
                     style={{ background: P.bg, color: '#2D3748' }}>
                  <P.Icon className="w-4 h-4" style={{ color: P.ring }} />
                  #{b.rank} · {P.label}
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <BookCardCover book={b} />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-medium mb-1 line-clamp-1" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                    {b.title}
                  </h3>
                  <p className="text-sm mb-3 line-clamp-1" style={{ color: '#718096' }}>
                    by {b.author_name}
                  </p>
                  <div className="flex items-center justify-between">
                    {b.category && (
                      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                            style={{ background: '#FDE68A', color: '#2D3748' }}>
                        {b.category}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: P.ring }}>
                      <Sparkles className="w-3.5 h-3.5" />
                      {b.share_count} shared
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Ranks 4–10 - compact list */}
        {rest.length > 0 && (
          <div className="bg-white rounded-3xl border overflow-hidden"
               style={{ borderColor: '#F3E8E0', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
               data-testid="leaderboard-rest-list">
            {rest.map((b, idx) => (
              <Link
                key={b.book_id}
                to={`/book/${b.book_id}`}
                data-testid={`leaderboard-row-${b.rank}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-orange-50 transition-colors"
                style={{ borderTop: idx === 0 ? 'none' : '1px solid #F3E8E0' }}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                     style={{ background: '#FFF4D1', color: '#8A6300' }}>
                  #{b.rank}
                </div>
                <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border" style={{ borderColor: '#F3E8E0' }}>
                  <BookCardCover book={b} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium truncate" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                    {b.title}
                  </div>
                  <div className="text-sm truncate" style={{ color: '#718096' }}>
                    by {b.author_name}
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-semibold" style={{ color: '#FF8E71' }}>
                    {b.share_count} shared
                  </div>
                  {b.category && (
                    <div className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>{b.category}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
